import React from 'react'
import {
    Blog,
    getLiveBlogIdFromLink,
    is24LiveBlog,
    LIVEBLOG24,
} from '../../content/Blog/Blog'
import { EmbedProps } from '../../publication/Embed/Embed'
import {
    StyledEmbed,
    StyledFacebookEmbed,
    StyledInstagramEmbed,
    StyledTwitterEmbed,
} from '../../publication/Embed/Embed.styled'
import { CityPulse, isCityPulse } from '../../publication/Embed/web/CityPulse'
import { isOmny, Omny } from '../../publication/Embed/web/Omny'
import {
    isShortstack,
    LazyShortStack,
} from '../../publication/Embed/web/Shortstack.lazy'
import { isWayIn, WayIn } from '../../publication/Embed/web/WayIn'
import { Embedly } from './Embedly'
import { LazyFacebookEmbed } from './Facebook/Facebook.lazy'
import { isACast } from './helpers/acast'
import { isFacebook } from './helpers/facebook'
import { isDeprecatedFanhubEmbed } from './helpers/fanhub'
import { isFlourish } from './helpers/flourish'
import { isInstagram, parseInstagramLink } from './helpers/instagram'
import { isOurLegacyLiveBlog } from './helpers/liveevent'
import { isTikTok } from './helpers/tikTok'
import { isTwitterUrl } from './helpers/twitter'
import { isWhistleOut } from './helpers/whistleout'
import { LazyInstagramEmbed } from './Instagram/Instagram.lazy'
import { LazyTwitterEmbed } from './Twitter/TwitterEmbed.lazy'
import { ACast } from './web/ACast'
import { DatawrapperEmbed, isDatawrapper } from './web/Datawrapper'
import Flourish from './web/Flourish'
import { isSlido, Slido } from './web/Slido'
import { isSpotify, Spotify } from './web/Spotify'
import { isSurveyMonkey, SurveyMonkeyJS } from './web/SurveyMonkeyJS'
import TikTok from './web/TikTok'
import { WhistleOut } from './web/WhistleOut'
import { isTypeform, Typeform } from './web/Typeform'
import { isShorthand, Shorthand } from './web/Shorthand'
import { isYoutube, Youtube } from './web/Youtube'
import { isElection } from './helpers/elections'
import { ElectionElectorateEmbed } from './web'
import classnames from 'classnames'

export const WebEmbed: React.FC<EmbedProps> = ({
    link,
    log,
    onEvent,
    adUnitPath,
    className,
}) => {
    if (!link || isOurLegacyLiveBlog(link) || isDeprecatedFanhubEmbed(link)) {
        return null
    }

    if (isACast(link)) {
        return (
            <StyledEmbed className={`${className} embed`}>
                <ACast link={link} />
            </StyledEmbed>
        )
    }

    if (isWhistleOut(link)) {
        return (
            <StyledEmbed className={`${className} embed`}>
                <WhistleOut link={link} />
            </StyledEmbed>
        )
    }

    if (isSurveyMonkey(link)) {
        return (
            <StyledEmbed className={`${className} embed`}>
                <SurveyMonkeyJS link={link} />
            </StyledEmbed>
        )
    }

    // Due to a height bug with embedly (SWM-4308) we are rendering omny in its own iframe
    if (isOmny(link)) {
        return (
            <StyledEmbed className={`${className} embed`}>
                <Omny link={link} />
            </StyledEmbed>
        )
    }

    if (isTypeform(link)) {
        return (
            <StyledEmbed className={`${className} embed`}>
                <Typeform link={link} />
            </StyledEmbed>
        )
    }

    if (isCityPulse(link)) {
        return (
            <StyledEmbed className={`${className} embed`}>
                <CityPulse link={link} />
            </StyledEmbed>
        )
    }

    if (isShortstack(link)) {
        return (
            <StyledEmbed className={`${className} embed`}>
                <LazyShortStack link={link} />
            </StyledEmbed>
        )
    }

    if (isWayIn(link)) {
        return (
            <StyledEmbed className={`${className} embed`}>
                <WayIn link={link} />
            </StyledEmbed>
        )
    }

    if (is24LiveBlog(link)) {
        const id = getLiveBlogIdFromLink(link)

        if (!id) {
            console.error('24 hour live blog ID could not be found.')
            return null
        }

        return (
            <StyledEmbed className={`${className} embed`}>
                <Blog blogId={id} provider={LIVEBLOG24} />
            </StyledEmbed>
        )
    }

    if (isSlido(link)) {
        return (
            <StyledEmbed className={`${className} embed`}>
                <Slido link={link} />
            </StyledEmbed>
        )
    }

    if (isTwitterUrl(link)) {
        return (
            <StyledTwitterEmbed className={`${className} embed`}>
                <LazyTwitterEmbed link={link} />
            </StyledTwitterEmbed>
        )
    }

    if (isInstagram(link)) {
        const { url } = parseInstagramLink(link)
        if (url) {
            return (
                <StyledInstagramEmbed className={`${className} embed`}>
                    <LazyInstagramEmbed link={url} />
                </StyledInstagramEmbed>
            )
        }
    }

    if (isFacebook(link)) {
        return (
            <StyledFacebookEmbed className={classnames(className, 'embed')}>
                <LazyFacebookEmbed link={link} />
            </StyledFacebookEmbed>
        )
    }

    if (isFlourish(link)) {
        return (
            <StyledEmbed className={className}>
                <Flourish link={link} />
            </StyledEmbed>
        )
    }

    if (isDatawrapper(link)) {
        return (
            <StyledEmbed className={`${className} embed`}>
                <DatawrapperEmbed link={link} />
            </StyledEmbed>
        )
    }

    if (isTikTok(link)) {
        return (
            <StyledEmbed className={`${className} embed`}>
                <TikTok link={link} />
            </StyledEmbed>
        )
    }

    if (isSpotify(link)) {
        return (
            <StyledEmbed className={`${className} embed`}>
                <Spotify link={link} />
            </StyledEmbed>
        )
    }

    if (isShorthand(link)) {
        const url = new URL(link)
        if (url.protocol === 'http:') {
            url.protocol = 'https:'
            log.warn(
                { link, newLink: url.toString() },
                'Shorthand embeds should be https; rewriting...',
            )
        }
        return <Shorthand className={className} link={url.toString()} />
    }

    if (isElection(link)) {
        return (
            <StyledEmbed className={`${className} embed`}>
                <ElectionElectorateEmbed link={link} onEvent={onEvent} />
            </StyledEmbed>
        )
    }

    if (isYoutube(link)) {
        return (
            <StyledEmbed className={`${className} embed`}>
                <Youtube link={link} />
            </StyledEmbed>
        )
    }

    return (
        <Embedly
            link={link}
            log={log}
            onEvent={onEvent}
            adUnitPath={adUnitPath}
            className={className}
        />
    )
}

export default WebEmbed
